// Curated talent lists — the third Talent Management tab. Three operational
// tables Gili (talent_management area) can VIEW + ADD + EDIT:
//   • VIP / priority ("Gili list") — add by username OR seller id, edit source/note,
//     toggle active (soft-remove). SCOUT reads active rows every search (no cache).
//   • Excluded — signed quality points + a hard-ban toggle (upsert by seller id).
//   • Opt-out — sellers never shown (add/remove a seller id).
// The first two carry a filter strip; opt-out is one column of ids, so there is
// nothing categorical to narrow and it deliberately has none.
// Wire fields are camelCase (the generated types); all writes go through
// /api/admin/talent/* (TalentAdminDep), keyed on id/username.
import { ShowMoreRow } from "../components/show-more-row";
import { type ReactNode, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyHomeEndKey,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  usePagedRows,
} from "@recruiter/shared";
import type { ExcludedListResponse, VipSellerRow } from "@recruiter/shared/types";

import {
  addOptout,
  addVipSeller,
  deactivateVipSeller,
  removeExcluded,
  removeOptout,
  updateVipSeller,
  upsertExcluded,
} from "../lib/api";
import { adminKeys, talentExcludedQuery, talentOptoutQuery, talentVipQuery } from "../lib/queries";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { EmptyState, TableSkeleton } from "../components/states";
import { useWriteAccess } from "../lib/use-access";
import "./talent-management.css";
import "./talent-lists.css";

/** Shared frame: title + the Talent Management tab bar. One table per tab. */
function Frame({ subtitle, children }: { subtitle: string; children: ReactNode }) {
  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Talent Management</h1>
        <p className="ad-page__subtitle">{subtitle}</p>
      </header>
      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />
      <div className="tl-stack">{children}</div>
    </div>
  );
}

export function VipListPage() {
  return (
    <Frame subtitle="VIP / priority sellers — the curated Gili list.">
      <VipTable />
    </Frame>
  );
}

export function ExcludedListPage() {
  return (
    <Frame subtitle="Excluded sellers — quality penalty or hard ban.">
      <ExcludedTable />
    </Frame>
  );
}

export function OptoutListPage() {
  return (
    <Frame subtitle="Opt-out sellers — never shown in any search.">
      <OptoutTable />
    </Frame>
  );
}

/** Two repeating columns on the VIP roster. Active is what the list is READ by
 *  once it has been curated for a while ("what is actually live right now"),
 *  and the soft-remove means retired rows accumulate in place rather than
 *  leaving. Source answers "everything Gili added" on a list several people
 *  write to. Username, seller id and note are one-per-row free text.
 *
 *  Active is CLOSED so both halves stay listed at zero; Source is derived and
 *  disappears on a list nobody has attributed. */
const VIP_FILTERS: readonly FilterColumn<VipSellerRow>[] = [
  {
    key: "active",
    label: "Filter by state",
    allLabel: "Any state",
    value: (r) => (r.active ? "active" : "removed"),
    options: [
      { value: "active", label: "Active" },
      { value: "removed", label: "Removed" },
    ],
  },
  {
    key: "addedBy",
    label: "Filter by source",
    allLabel: "Any source",
    value: (r) => r.addedBy,
    noValueLabel: "Not recorded",
  },
];

/** Not aliased in `@recruiter/shared/types`, so it is read off the response
 *  rather than redeclared here. */
type ExcludedRow = ExcludedListResponse["rows"][number];

/** One control: a hard ban and a points penalty are different decisions, and
 *  "show me everyone we actually banned" is the one worth isolating. Seller id
 *  is one per row and points are numeric. */
const EXCLUDED_FILTERS: readonly FilterColumn<ExcludedRow>[] = [
  {
    key: "hard",
    label: "Filter by exclusion",
    allLabel: "Any exclusion",
    value: (r) => (r.hard ? "hard" : "points"),
    options: [
      { value: "hard", label: "Hard ban" },
      { value: "points", label: "Points penalty" },
    ],
  },
];

// ── VIP / priority ──────────────────────────────────────────────────────────────
function VipTable() {
  const qc = useQueryClient();
  const q = useQuery(talentVipQuery);
  // The three rosters on this page are the WRITE half of `talent_management`
  // (migration 0120): reading them is `read`, every edit is `full`. An analyst
  // granted Read only watches the roster and changes nothing, so the add form and
  // the row controls are not drawn — each of them would 403.
  //
  // `known` holds back the empty-state DIRECTIONS ("Add a username above") until
  // the grant query lands: pointing at a form that is not there, or telling an
  // editor there is nothing to do, is a claim about their access rather than a
  // control we can safely leave out.
  const { canWrite: canEdit, known: accessKnown } = useWriteAccess("talent_management");
  const invalidate = () => void qc.invalidateQueries({ queryKey: adminKeys.talentVip });
  const add = useMutation({ mutationFn: addVipSeller, onSuccess: invalidate });
  const update = useMutation({
    mutationFn: (v: { id: number; addedBy: string; note: string; active: boolean }) =>
      updateVipSeller(v.id, { addedBy: v.addedBy, note: v.note, active: v.active }),
    onSuccess: invalidate,
  });
  const deactivate = useMutation({ mutationFn: deactivateVipSeller, onSuccess: invalidate });

  const [username, setUsername] = useState("");
  const [sellerId, setSellerId] = useState("");
  const [source, setSource] = useState("");
  const [note, setNote] = useState("");

  const rows = q.data?.rows ?? [];
  const filters = useColumnFilters(rows, VIP_FILTERS, { restore: "talent-lists/vip/filters" });
  const paged = usePagedRows(filters.rows, { restore: "talent-lists/vip/window" });
  return (
    <section className="ad-section tl-card" aria-label="VIP priority sellers">
      <div className="tm-section-head">
        <h2>VIP / priority sellers</h2>
        <span className="tl-count">{rows.filter((r) => r.active).length} active</span>
      </div>
      <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
      {canEdit ? (
        <form
          className="tl-addrow"
          onSubmit={(e) => {
            e.preventDefault();
            if (!username.trim() && !sellerId.trim()) return;
            add.mutate(
              {
                username: username.trim(),
                fiverrSellerId: sellerId.trim(),
                addedBy: source.trim(),
                note: note.trim(),
              },
              {
                onSuccess: () => {
                  setUsername("");
                  setSellerId("");
                  setSource("");
                  setNote("");
                },
              },
            );
          }}
        >
          <input
            className="tl-in"
            placeholder="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <input
            className="tl-in"
            placeholder="seller id (optional)"
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <input
            className="tl-in"
            placeholder="source (e.g. Gili / CSM)"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <input
            className="tl-in tl-in--wide"
            placeholder="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <button
            className="tl-btn"
            type="submit"
            disabled={add.isPending || (!username.trim() && !sellerId.trim())}
          >
            Add
          </button>
        </form>
      ) : null}
      {q.isLoading ? (
        <TableSkeleton columns={canEdit ? 6 : 5} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No VIP sellers"
          message={
            accessKnown
              ? canEdit
                ? "Add a username or seller id above."
                : "Nobody is on the priority roster."
              : undefined
          }
        />
      ) : filters.rows.length === 0 ? (
        <EmptyState title="No VIP sellers match those filters" />
      ) : (
        <>
          <table className="rc-table tl-table">
            <TableExport name="talent-vip" label="the VIP list" />
            <thead>
              <tr>
                <th>Username</th>
                <th>Seller id</th>
                <th>Source</th>
                <th>Note</th>
                <th className="rc-num">Active</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {paged.shown.map((r) => (
                <tr key={r.id} className={r.active ? "" : "tl-row--off"}>
                  <td>{r.username || "-"}</td>
                  <td>{r.fiverrSellerId || "-"}</td>
                  <td>{r.addedBy || "-"}</td>
                  <td>{r.note || "-"}</td>
                  <td className="rc-num">
                    {canEdit ? (
                      <input
                        type="checkbox"
                        checked={r.active}
                        onChange={(e) =>
                          update.mutate({
                            id: r.id,
                            addedBy: r.addedBy,
                            note: r.note,
                            active: e.target.checked,
                          })
                        }
                      />
                    ) : r.active ? (
                      "yes"
                    ) : (
                      "-"
                    )}
                  </td>
                  {canEdit ? (
                    <td>
                      <button
                        className="tl-link"
                        onClick={() => deactivate.mutate(r.id)}
                        disabled={!r.active}
                      >
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </>
      )}
    </section>
  );
}

// ── Excluded (quality points + hard ban) ─────────────────────────────────────────
function ExcludedTable() {
  const qc = useQueryClient();
  const q = useQuery(talentExcludedQuery);
  const { canWrite: canEdit, known: accessKnown } = useWriteAccess("talent_management");
  const invalidate = () => void qc.invalidateQueries({ queryKey: adminKeys.talentExcluded });
  const upsert = useMutation({ mutationFn: upsertExcluded, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: removeExcluded, onSuccess: invalidate });

  const [sellerId, setSellerId] = useState("");
  const [points, setPoints] = useState("0");
  const [hard, setHard] = useState(false);

  const rows = q.data?.rows ?? [];
  const filters = useColumnFilters(rows, EXCLUDED_FILTERS, {
    restore: "talent-lists/excluded/filters",
  });
  const paged = usePagedRows(filters.rows, { restore: "talent-lists/excluded/window" });
  return (
    <section className="ad-section tl-card" aria-label="Excluded sellers">
      <div className="tm-section-head">
        <h2>Excluded (quality penalty / ban)</h2>
        <span className="tl-count">{rows.length}</span>
      </div>
      <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
      {canEdit ? (
        <form
          className="tl-addrow"
          onSubmit={(e) => {
            e.preventDefault();
            if (!sellerId.trim()) return;
            upsert.mutate(
              { sellerId: sellerId.trim(), points: Number(points) || 0, hard },
              {
                onSuccess: () => {
                  setSellerId("");
                  setPoints("0");
                  setHard(false);
                },
              },
            );
          }}
        >
          <input
            className="tl-in"
            placeholder="seller id"
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <input
            className="tl-in tl-in--num"
            type="number"
            placeholder="points"
            value={points}
            onChange={(e) => setPoints(e.target.value)}
          />
          <label className="tl-check">
            <input type="checkbox" checked={hard} onChange={(e) => setHard(e.target.checked)} />{" "}
            hard ban
          </label>
          <button className="tl-btn" type="submit" disabled={upsert.isPending || !sellerId.trim()}>
            Save
          </button>
        </form>
      ) : null}
      {q.isLoading ? (
        <TableSkeleton columns={canEdit ? 4 : 3} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No exclusions"
          message={
            accessKnown
              ? canEdit
                ? "Add a seller id with a points delta or a hard ban."
                : "No seller carries a quality penalty or a ban."
              : undefined
          }
        />
      ) : filters.rows.length === 0 ? (
        <EmptyState title="No exclusions match those filters" />
      ) : (
        <>
          <table className="rc-table tl-table">
            <TableExport name="talent-excluded" label="the exclusion list" />
            <thead>
              <tr>
                <th>Seller id</th>
                <th className="rc-num">Points</th>
                <th className="rc-num">Hard ban</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {paged.shown.map((r) => (
                <tr key={r.sellerId}>
                  <td>{r.sellerId}</td>
                  <td className="rc-num">{r.points}</td>
                  <td className="rc-num">{r.hard ? "yes" : "-"}</td>
                  {canEdit ? (
                    <td>
                      <button className="tl-link" onClick={() => remove.mutate(r.sellerId)}>
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </>
      )}
    </section>
  );
}

// ── Opt-out ──────────────────────────────────────────────────────────────────────
function OptoutTable() {
  const qc = useQueryClient();
  const q = useQuery(talentOptoutQuery);
  const { canWrite: canEdit, known: accessKnown } = useWriteAccess("talent_management");
  const invalidate = () => void qc.invalidateQueries({ queryKey: adminKeys.talentOptout });
  const add = useMutation({ mutationFn: addOptout, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: removeOptout, onSuccess: invalidate });

  const [sellerId, setSellerId] = useState("");
  const ids: string[] = q.data?.sellerIds ?? [];
  const paged = usePagedRows(ids, { restore: "talent-lists/optout/window" });
  return (
    <section className="ad-section tl-card" aria-label="Opt-out sellers">
      <div className="tm-section-head">
        <h2>Opt-out (never shown)</h2>
        <span className="tl-count">{ids.length}</span>
      </div>
      {canEdit ? (
        <form
          className="tl-addrow"
          onSubmit={(e) => {
            e.preventDefault();
            if (!sellerId.trim()) return;
            add.mutate({ sellerId: sellerId.trim() }, { onSuccess: () => setSellerId("") });
          }}
        >
          <input
            className="tl-in"
            placeholder="seller id"
            value={sellerId}
            onChange={(e) => setSellerId(e.target.value)}
            onKeyDown={applyHomeEndKey}
          />
          <button className="tl-btn" type="submit" disabled={add.isPending || !sellerId.trim()}>
            Add
          </button>
        </form>
      ) : null}
      {q.isLoading ? (
        <TableSkeleton columns={canEdit ? 2 : 1} />
      ) : ids.length === 0 ? (
        <EmptyState
          title="No opt-outs"
          message={
            accessKnown
              ? canEdit
                ? "Add a seller id to hide them from every search."
                : "No talent has opted out of being shown."
              : undefined
          }
        />
      ) : (
        <>
          <table className="rc-table tl-table">
            <TableExport name="talent-optout" label="the opt-out list" />
            <thead>
              <tr>
                <th>Seller id</th>
                {canEdit ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {paged.shown.map((id) => (
                <tr key={id}>
                  <td>{id}</td>
                  {canEdit ? (
                    <td>
                      <button className="tl-link" onClick={() => remove.mutate(id)}>
                        Remove
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </>
      )}
    </section>
  );
}
